import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, Bot } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [userName, setUserName] = useState("");
  const [needsName, setNeedsName] = useState(true);
  const messagesEndRef = useRef(null);

  // Load session from localStorage on mount
  useEffect(() => {
    const savedSession = localStorage.getItem("denhi_session");
    if (savedSession) {
      const session = JSON.parse(savedSession);
      setSessionId(session.sessionId);
      setUserName(session.userName);
      setMessages(session.messages || []);
      setNeedsName(!session.userName);
    } else {
      // Initial greeting
      setMessages([
        {
          role: "assistant",
          content: "Merhaba! Ben DenHi, Hisar Dental Lab'ın dijital asistanıyım. Sizinle tanışmak isterim! Lütfen adınızı söyler misiniz? Adınızı bilmeden konuşmaya devam edemiyorum.",
        },
      ]);
    }
  }, []);

  // Save session to localStorage whenever it changes
  useEffect(() => {
    if (sessionId || messages.length > 1) {
      localStorage.setItem("denhi_session", JSON.stringify({
        sessionId,
        userName,
        messages
      }));
    }
  }, [sessionId, userName, messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    
    // If we need a name, the first message is the name
    if (needsName) {
      // Hızlı ret kontrolü (anında geri bildirim)
      const refusalPatterns = [
        "hayır", "yok", "söylemem", "söylemicem", "söylemeyeceğim", "söylemiyorum",
        "istemiyorum", "gerek yok", "pas", "vermem", "vermeyeceğim", "vermiyorum",
        "boşver", "geç", "atlat", "bilmiyorsun", "bilmezsin", "öğrenemezsin",
        "adım yok", "adımı söylemem", "adımı vermem", "ad yok", "olmaz", "yoo"
      ];
      const lowerMessage = userMessage.toLowerCase();
      const isRefusal = refusalPatterns.some(word => lowerMessage.includes(word)) ||
                        lowerMessage.length < 2 ||
                        /^[0-9]+$/.test(userMessage);

      if (isRefusal) {
        setMessages((prev) => [
          ...prev,
          { role: "user", content: userMessage },
          { role: "assistant", content: "Anlıyorum ama adınızı bilmeden size yardımcı olmam mümkün değil. Merak etmeyin, sadece bu sohbette kullanacağım. Lütfen adınızı söyleyin, yoksa konuşamayız. 😊" }
        ]);
        return;
      }

      // Kullanıcının yazdığını göster ve Gemini ile gerçek isim mi diye doğrula
      setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
      setLoading(true);

      let isValidName = false;
      try {
        const validation = await axios.post(`${API}/validate-name`, { name: userMessage });
        isValidName = validation.data?.is_valid === true;
      } catch (error) {
        console.error("Name validation error:", error);
        // Hata durumunda basit kontrole düş
        isValidName = /^[a-zA-ZğüşöçıİĞÜŞÖÇ\s]{2,}$/.test(userMessage);
      }

      if (!isValidName) {
        setLoading(false);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Bu gerçek bir isim gibi görünmüyor. 🙂 Lütfen gerçek adınızı söyleyin — adınızı bilmeden konuşmaya devam edemem." }
        ]);
        return;
      }

      // İsmi kabul et
      setUserName(userMessage);
      setNeedsName(false);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Memnun oldum ${userMessage}! 😊 Size nasıl yardımcı olabilirim? Hizmetlerimiz, fiyatlarımız veya iletişim bilgilerimiz hakkında sorularınızı yanıtlayabilirim.` }
      ]);

      // Backend'e ismi kaydet
      try {
        const response = await axios.post(`${API}/chat`, {
          message: `Benim adım ${userMessage}`,
          session_id: sessionId,
          user_name: userMessage,
        });
        setSessionId(response.data.session_id);
      } catch (error) {
        console.error("Session error:", error);
      } finally {
        setLoading(false);
      }
      return;
    }

    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId,
        user_name: userName,
      });

      setSessionId(response.data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.data.response },
      ]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Üzgünüm, bir hata oluştu. Lütfen daha sonra tekrar deneyin veya bizi arayın: +90 532 394 83 29",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full shadow-lg shadow-cyan-500/30 flex items-center justify-center hover:shadow-xl hover:shadow-cyan-500/40 transition-shadow"
            data-testid="chatbot-button"
            aria-label="Chatbot'u aç"
          >
            <MessageCircle className="w-7 h-7 text-white" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            style={{ maxWidth: '100vw' }}
            className="fixed bottom-0 left-0 right-0 sm:left-auto sm:bottom-6 sm:right-6 z-50 w-full sm:w-[360px] h-[85vh] sm:h-[500px] bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden sm:border border-slate-200"
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0A192F] to-cyan-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">DenHi</h3>
                  <p className="text-white/70 text-xs">
                    {userName ? `${userName} ile sohbet` : "Dijital Asistan"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/70 hover:text-white transition-colors"
                data-testid="chatbot-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                      msg.role === "user"
                        ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-br-md"
                        : "bg-white text-slate-700 shadow-sm border border-slate-100 rounded-bl-md"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white text-slate-700 shadow-sm border border-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                    <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={needsName ? "Adınızı yazın..." : "Mesajınızı yazın..."}
                  className="flex-1 bg-slate-100 border-0 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-cyan-500/50"
                  data-testid="chatbot-input"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white disabled:opacity-50 hover:shadow-lg transition-shadow"
                  data-testid="chatbot-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
